import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  cashFlowPeriodDisplayLabel,
  computeCashFlowDateRange,
  isCashFlowCalendarYearPeriod,
} from './cashFlowDateRange';
import { formatCurrency } from '../utils/formatters';
import EmptyState from '../components/ui/EmptyState';
// Removed income pie chart while redesigning legend-only view

// Safe currency formatter that handles NaN values
const safeFormatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return formatCurrency(0);
  }
  return formatCurrency(amount);
};

interface CashFlowSummary {
  month: string;
  income: number;
  expenses: number;
  transfers: number;
  net_cash_flow: number;
  savings_rate: number;
  transaction_count: number;
}

interface CategoryBreakdown {
  category_id: number;
  category_name: string;
  category_color: string;
  total_amount: number;
  transaction_count: number;
  percentage: number;
}

interface IncomeSource {
  category_id: number | null;
  category_name: string;
  category_color: string | null;
  subcategory_id: number | null;
  subcategory_name: string | null;
  total_amount: string | number;
  transaction_count: string | number;
  avg_amount: string | number;
  first_transaction: string;
  last_transaction: string;
  is_reimbursement?: boolean;
}

interface ExpenseDrillDown {
  category_id: number;
  category_name: string;
  category_color: string;
  subcategory_id: number | null;
  subcategory_name: string | null;
  total_amount: string | number;
  transaction_count: string | number;
  avg_amount: string | number;
  first_transaction: string;
  last_transaction: string;
  total_reimbursed_amount?: number;
}

const CashFlow2: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cashFlowSummary, setCashFlowSummary] = useState<CashFlowSummary[]>([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState<CategoryBreakdown[]>([]);
  const [incomeSources, setIncomeSources] = useState<IncomeSource[]>([]);
  const [expensesDrillDown, setExpensesDrillDown] = useState<ExpenseDrillDown[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('last6months');
  const [transactionYears, setTransactionYears] = useState<number[] | null>(null);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [viewMode, setViewMode] = useState<'monthly' | 'yearly'>('monthly');
  const [activeTab, setActiveTab] = useState<'overview' | 'income' | 'expenses'>('overview');
  const [selectedIncomeSource, setSelectedIncomeSource] = useState<string | null>(null);
  const [selectedExpenseCategory, setSelectedExpenseCategory] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [expandedIncomeCategories, setExpandedIncomeCategories] = useState<Set<number>>(new Set());
  const [transactions, setTransactions] = useState<any[]>([]);

  // Set default dates when custom is selected
  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    if (period === 'custom') {
      // Set default to last 30 days
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      setCustomStartDate(startDate.toISOString().split('T')[0]);
      setCustomEndDate(endDate.toISOString().split('T')[0]);
    }
  };

  useEffect(() => {
    if (!user) {
      setTransactionYears(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { years } = await api.transactions.getTransactionYears();
        if (!cancelled) {
          setTransactionYears(Array.isArray(years) ? years : []);
        }
      } catch {
        if (!cancelled) setTransactionYears([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (transactionYears === null) return;
    if (!isCashFlowCalendarYearPeriod(selectedPeriod)) return;
    const y = parseInt(selectedPeriod, 10);
    if (!transactionYears.includes(y)) {
      setSelectedPeriod(
        transactionYears.length > 0 ? String(Math.max(...transactionYears)) : 'last6months'
      );
    }
  }, [transactionYears, selectedPeriod]);

  useEffect(() => {
    if (user) {
      loadCashFlowData();
    }
  }, [user, selectedPeriod, customStartDate, customEndDate]);

  const loadCashFlowData = async () => {
    try {
      setLoading(true);
      setError(null);

      const range = computeCashFlowDateRange({
        selectedPeriod,
        customStartDate,
        customEndDate,
        now: new Date(),
      });
      if (!range.ok) {
        setError(range.error);
        return;
      }
      const { startDate, endDate } = range;

      // Load cash flow summary
      const summaryResponse = await api.transactions.getCashFlowSummary({ startDate, endDate });
      setCashFlowSummary(summaryResponse);

      // Load category breakdown
      const breakdownResponse = await api.transactions.getCategoryBreakdown({ startDate, endDate });
      setCategoryBreakdown(breakdownResponse);

      // Load income sources breakdown
      const incomeSourcesResponse = await api.transactions.getIncomeSourcesBreakdown({ startDate, endDate });
      console.log('Income sources response:', incomeSourcesResponse);
      setIncomeSources(incomeSourcesResponse);

      // Load expenses drill down
      const expensesDrillDownResponse = await api.transactions.getExpensesDrillDown({ startDate, endDate });
      setExpensesDrillDown(expensesDrillDownResponse);

      // Load transactions for drill-down functionality
      // The endpoint is cursor-paginated ({ transactions, hasMore, nextCursor }); fetch every page in range.
      const periodTransactions: any[] = [];
      let cursor: { date: string; id: number } | null = null;
      do {
        const page: { transactions?: any[]; hasMore?: boolean; nextCursor?: { date: string; id: number } | null } =
          await api.transactions.getTransactions({
            startDate,
            endDate,
            ...(cursor ? { cursorDate: cursor.date, cursorId: cursor.id } : {}),
          });
        periodTransactions.push(...(page?.transactions ?? []));
        cursor = page?.hasMore ? page.nextCursor ?? null : null;
      } while (cursor);
      setTransactions(periodTransactions);

    } catch (err) {
      console.error('Error loading cash flow data:', err);
      setError('Failed to load cash flow data');
    } finally {
      setLoading(false);
    }
  };

  // Filter transactions by income source (now by category)
  const getTransactionsByIncomeSource = (sourceName: string) => {
    
    const filtered = transactions.filter(t => {
      const isIncome = t.amount > 0;
      
      // Handle "Uncategorized Income" - transactions with no category
      if (sourceName === 'Uncategorized Income') {
        const isUncategorized = !t.category_name || t.category_name === null || t.category_name === '';
        const match = isIncome && isUncategorized;
        
        
        return match;
      } else if (sourceName.includes(' - ')) {
        // Handle subcategory selection (format: "Category - Subcategory")
        // Split only on the first " - " to preserve subcategory names that contain " - "
        const firstDashIndex = sourceName.indexOf(' - ');
        const categoryPart = sourceName.substring(0, firstDashIndex);
        const subcategoryPart = sourceName.substring(firstDashIndex + 3); // +3 to skip " - "
        const categoryMatch = t.category_name === categoryPart;
        const subcategoryMatch = t.subcategory_name === subcategoryPart;
        const match = isIncome && categoryMatch && subcategoryMatch;
        
        
        return match;
      } else {
        // Handle specific category names (main category only)
        const categoryMatch = t.category_name === sourceName;
        const match = isIncome && categoryMatch;
        
        
        return match;
      }
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    return filtered;
  };

  // Calculate refund and reimbursement summary for income source
  const getRefundSummary = (sourceName: string) => {
    const allTransactions = getTransactionsByIncomeSource(sourceName);
    const refunds = allTransactions.filter(t => t.is_refund);
    const reimbursements = allTransactions.filter(t => t.is_insurance_reimbursement);
    const nonRefunds = allTransactions.filter(t => !t.is_refund && !t.is_insurance_reimbursement);
    
    const refundCount = refunds.length;
    const reimbursementCount = reimbursements.length;
    const refundTotal = refunds.reduce((sum, t) => sum + Number(t.amount), 0);
    const reimbursementTotal = reimbursements.reduce((sum, t) => sum + Number(t.amount), 0);
    const nonRefundTotal = nonRefunds.reduce((sum, t) => sum + Number(t.amount), 0);
    
    // Get the backend-calculated total from incomeSources (excludes refunds and reimbursements)
    const backendTotal = incomeSources
      .filter(income => {
        if (sourceName === 'Uncategorized Income') {
          return !income.category_name || income.category_name === 'Uncategorized Income';
        } else if (sourceName.includes(' - ')) {
          const firstDashIndex = sourceName.indexOf(' - ');
          const categoryPart = sourceName.substring(0, firstDashIndex);
          const subcategoryPart = sourceName.substring(firstDashIndex + 3);
          return income.category_name === categoryPart && income.subcategory_name === subcategoryPart;
        } else {
          return income.category_name === sourceName;
        }
      })
      .reduce((sum, income) => sum + (typeof income.total_amount === 'string' ? parseFloat(income.total_amount) : income.total_amount), 0);
    
    return {
      totalTransactions: allTransactions.length,
      refundCount,
      refundTotal,
      reimbursementCount,
      reimbursementTotal,
      nonRefundTotal,
      totalAmount: backendTotal // Backend already excludes refunds and reimbursements
    };
  };

  // Calculate refund summary for expense category
  const getExpenseRefundSummary = (categoryName: string) => {
    const allTransactions = getTransactionsByExpenseCategory(categoryName);
    const refunds = allTransactions.filter(t => t.is_refund);
    const nonRefunds = allTransactions.filter(t => !t.is_refund);
    
    // Calculate totals using split amounts when applicable
    const refundTotal = Math.abs(refunds.reduce((sum, t) => {
      const splitAmount = getSplitAmountForCategory(t, categoryName);
      const amount = splitAmount !== null ? splitAmount : Math.abs(Number(t.amount));
      return sum + amount;
    }, 0));
    
    const nonRefundTotal = Math.abs(nonRefunds.reduce((sum, t) => {
      const splitAmount = getSplitAmountForCategory(t, categoryName);
      const amount = splitAmount !== null ? splitAmount : Math.abs(Number(t.amount));
      return sum + amount;
    }, 0));
    
    // Get the backend-calculated total from expensesDrillDown (already includes splits)
    const backendTotal = expensesDrillDown
      .filter(expense => {
        if (categoryName === 'Uncategorized Expenses') {
          return !expense.category_name || expense.category_name === 'Uncategorized Expenses';
        } else if (categoryName.includes(' - ')) {
          const firstDashIndex = categoryName.indexOf(' - ');
          const categoryPart = categoryName.substring(0, firstDashIndex);
          const subcategoryPart = categoryName.substring(firstDashIndex + 3);
          return expense.category_name === categoryPart && expense.subcategory_name === subcategoryPart;
        } else {
          return expense.category_name === categoryName;
        }
      })
      .reduce((sum, expense) => sum + (typeof expense.total_amount === 'string' ? parseFloat(expense.total_amount) : expense.total_amount), 0);
    
    return {
      totalTransactions: allTransactions.length,
      refundCount: refunds.length,
      refundTotal,
      nonRefundTotal,
      totalAmount: backendTotal // Use backend-calculated total which handles refunds and splits correctly
    };
  };

  // Helper function to get the split amount for a transaction matching a specific category
  const getSplitAmountForCategory = (transaction: any, categoryName: string): number | null => {
    if (!transaction.splits || transaction.splits.length === 0) {
      return null; // No splits, use full transaction amount
    }

    if (categoryName === 'Uncategorized Expenses') {
      return null; // Uncategorized transactions don't have splits
    }

    if (categoryName.includes(' - ')) {
      // Handle subcategory selection (format: "Category - Subcategory")
      const firstDashIndex = categoryName.indexOf(' - ');
      const categoryPart = categoryName.substring(0, firstDashIndex);
      const subcategoryPart = categoryName.substring(firstDashIndex + 3);
      
      // Sum all splits matching this category and subcategory
      const matchingSplits = transaction.splits.filter((split: any) => 
        split.category_name === categoryPart && split.subcategory_name === subcategoryPart
      );
      
      if (matchingSplits.length === 0) {
        return null; // No matching splits, use full transaction amount
      }
      
      return matchingSplits.reduce((sum: number, split: any) => {
        const amount = typeof split.amount === 'string' ? parseFloat(split.amount) : split.amount;
        return sum + Math.abs(amount);
      }, 0);
    } else {
      // Handle category selection (main category only)
      // Sum all splits matching this category (any subcategory)
      const matchingSplits = transaction.splits.filter((split: any) => 
        split.category_name === categoryName
      );
      
      if (matchingSplits.length === 0) {
        return null; // No matching splits, use full transaction amount
      }
      
      return matchingSplits.reduce((sum: number, split: any) => {
        const amount = typeof split.amount === 'string' ? parseFloat(split.amount) : split.amount;
        return sum + Math.abs(amount);
      }, 0);
    }
  };

  // Helper function to get the category name for display (from split if applicable)
  const getCategoryNameForDisplay = (transaction: any, categoryName: string): string => {
    if (categoryName === 'Uncategorized Expenses') {
      return 'Uncategorized';
    }

    if (categoryName.includes(' - ')) {
      // For subcategory, return the full "Category - Subcategory" format
      return categoryName;
    } else {
      // For category, check if we should show subcategory from split
      if (transaction.splits && transaction.splits.length > 0) {
        // Find first matching split to get subcategory if available
        const matchingSplit = transaction.splits.find((split: any) => 
          split.category_name === categoryName
        );
        if (matchingSplit && matchingSplit.subcategory_name) {
          return `${categoryName} - ${matchingSplit.subcategory_name}`;
        }
      }
      return categoryName;
    }
  };

  // Filter transactions by expense category or subcategory
  // Includes transactions with direct category assignment AND transactions with splits matching the category
  const getTransactionsByExpenseCategory = (categoryName: string) => {
    
    const filtered = transactions.filter(t => {
      const isExpense = t.amount < 0;
      
      // Handle "Uncategorized Expenses" - transactions with no category AND no splits
      if (categoryName === 'Uncategorized Expenses') {
        const hasDirectCategory = !!(t.category_name && t.category_name !== '');
        const hasSplits = t.splits && t.splits.length > 0;
        const isUncategorized = !hasDirectCategory && !hasSplits;
        const match = isExpense && isUncategorized;
        
        return match;
      } else if (categoryName.includes(' - ')) {
        // Handle subcategory selection (format: "Category - Subcategory")
        // Split only on the first " - " to preserve subcategory names that contain " - "
        const firstDashIndex = categoryName.indexOf(' - ');
        const categoryPart = categoryName.substring(0, firstDashIndex);
        const subcategoryPart = categoryName.substring(firstDashIndex + 3); // +3 to skip " - "
        
        // Check direct category assignment
        const directCategoryMatch = t.category_name === categoryPart;
        const directSubcategoryMatch = t.subcategory_name === subcategoryPart;
        const hasDirectMatch = directCategoryMatch && directSubcategoryMatch;
        
        // Check splits
        const hasSplitMatch = t.splits && t.splits.some((split: any) => 
          split.category_name === categoryPart && split.subcategory_name === subcategoryPart
        );
        
        const match = isExpense && (hasDirectMatch || hasSplitMatch);
        
        return match;
      } else {
        // Handle specific category names (main category only)
        // Check direct category assignment
        const directCategoryMatch = t.category_name === categoryName;
        
        // Check splits
        const hasSplitMatch = t.splits && t.splits.some((split: any) => 
          split.category_name === categoryName
        );
        
        const match = isExpense && (directCategoryMatch || hasSplitMatch);
        
        return match;
      }
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    
    return filtered;
  };

  // Process income data to group by categories and subcategories
  const processIncomeData = () => {
    if (!incomeSources || incomeSources.length === 0) {
      return [];
    }
    const categoryMap = new Map();
    
    incomeSources.forEach(income => {
      const categoryId = income.category_id || 'uncategorized';
      const categoryKey = `${categoryId}_${income.is_reimbursement ? 'reimb' : 'regular'}`;
      
      if (!categoryMap.has(categoryKey)) {
        categoryMap.set(categoryKey, {
          category_id: income.category_id,
          category_name: income.category_name,
          category_color: income.category_color,
          total_amount: 0,
          transaction_count: 0,
          first_transaction: income.first_transaction,
          last_transaction: income.last_transaction,
          is_reimbursement: income.is_reimbursement || false,
          subcategories: []
        });
      }
      
      const category = categoryMap.get(categoryKey);
      
      // Calculate refund-adjusted amount for this income source
      // For reimbursements, use the raw amount since they're already excluded from totals
      const adjustedAmount = income.is_reimbursement 
        ? (typeof income.total_amount === 'string' ? parseFloat(income.total_amount) : income.total_amount)
        : (() => {
            const sourceName = income.subcategory_name ? `${income.category_name} - ${income.subcategory_name}` : income.category_name;
            const refundSummary = getRefundSummary(sourceName);
            return refundSummary.totalAmount; // This excludes refunds
          })();
      
      if (income.subcategory_id && income.subcategory_name) {
        // This is a subcategory entry
        category.subcategories.push({
          subcategory_id: income.subcategory_id,
          subcategory_name: income.subcategory_name,
          total_amount: adjustedAmount,
          transaction_count: typeof income.transaction_count === 'string' ? parseInt(income.transaction_count) : income.transaction_count,
          first_transaction: income.first_transaction,
          last_transaction: income.last_transaction
        });
        // Add subcategory amount to category total
        category.total_amount += adjustedAmount;
        category.transaction_count += typeof income.transaction_count === 'string' ? parseInt(income.transaction_count) : income.transaction_count;
      } else {
        // This is a category-level entry (no subcategory)
        category.total_amount += adjustedAmount;
        category.transaction_count += typeof income.transaction_count === 'string' ? parseInt(income.transaction_count) : income.transaction_count;
      }
    });
    
    // No need to calculate averages since we removed the avg_amount column
    
    const result = Array.from(categoryMap.values())
      .filter(category => category.total_amount > 0.01 || category.is_reimbursement) // Show categories with income OR reimbursements
      .sort((a, b) => {
        // Sort reimbursements to the bottom
        if (a.is_reimbursement && !b.is_reimbursement) return 1;
        if (!a.is_reimbursement && b.is_reimbursement) return -1;
        return b.total_amount - a.total_amount;
      });
    return result;
  };

  // Process expenses data to group by categories and subcategories
  const processExpensesData = () => {
    const categoryMap = new Map();
    
    // Process categorized expenses from server data
    if (expensesDrillDown && expensesDrillDown.length > 0) {
      expensesDrillDown.forEach(expense => {
        const categoryId = expense.category_id;
        
        if (!categoryMap.has(categoryId)) {
          categoryMap.set(categoryId, {
            category_id: expense.category_id,
            category_name: expense.category_name,
            category_color: expense.category_color,
            total_amount: 0,
            total_reimbursed_amount: 0,
            transaction_count: 0,
            first_transaction: expense.first_transaction,
            last_transaction: expense.last_transaction,
            subcategories: []
          });
        }
        
        const category = categoryMap.get(categoryId);
        const reimbursementAmount = expense.total_reimbursed_amount ? (typeof expense.total_reimbursed_amount === 'string' ? parseFloat(expense.total_reimbursed_amount) : expense.total_reimbursed_amount) : 0;
        
        if (expense.subcategory_id && expense.subcategory_name) {
          // This is a subcategory entry
          category.subcategories.push({
            subcategory_id: expense.subcategory_id,
            subcategory_name: expense.subcategory_name,
            total_amount: typeof expense.total_amount === 'string' ? parseFloat(expense.total_amount) : expense.total_amount,
            total_reimbursed_amount: reimbursementAmount,
            transaction_count: typeof expense.transaction_count === 'string' ? parseInt(expense.transaction_count) : expense.transaction_count,
            first_transaction: expense.first_transaction,
            last_transaction: expense.last_transaction
          });
          // Add subcategory amount to category total
          category.total_amount += typeof expense.total_amount === 'string' ? parseFloat(expense.total_amount) : expense.total_amount;
          category.total_reimbursed_amount += reimbursementAmount;
          category.transaction_count += typeof expense.transaction_count === 'string' ? parseInt(expense.transaction_count) : expense.transaction_count;
        } else {
          // This is a category-level entry (no subcategory)
          category.total_amount += typeof expense.total_amount === 'string' ? parseFloat(expense.total_amount) : expense.total_amount;
          category.total_reimbursed_amount += reimbursementAmount;
          category.transaction_count += typeof expense.transaction_count === 'string' ? parseInt(expense.transaction_count) : expense.transaction_count;
        }
      });
    }
    
    // Add uncategorized expenses from transactions data
    // Only include transactions with no category AND no splits
    if (transactions && transactions.length > 0) {
      const uncategorizedExpenses = transactions.filter(t => 
        t.amount < 0 && 
        (!t.category_name || t.category_name === null || t.category_name === '') &&
        (!t.splits || t.splits.length === 0) && // Exclude transactions with splits
        !t.is_transfer && 
        !t.refund_transaction_id
      );
      
      if (uncategorizedExpenses.length > 0) {
        const totalAmount = uncategorizedExpenses.reduce((sum, t) => sum + Math.abs(t.amount), 0);
        const firstTransaction = uncategorizedExpenses.reduce((earliest, t) => 
          new Date(t.date) < new Date(earliest.date) ? t : earliest
        );
        const lastTransaction = uncategorizedExpenses.reduce((latest, t) => 
          new Date(t.date) > new Date(latest.date) ? t : latest
        );
        
        categoryMap.set('uncategorized', {
          category_id: null,
          category_name: 'Uncategorized Expenses',
          category_color: '#6B7280', // Gray color for uncategorized
          total_amount: totalAmount,
          transaction_count: uncategorizedExpenses.length,
          first_transaction: firstTransaction.date,
          last_transaction: lastTransaction.date,
          subcategories: []
        });
      }
    }
    
    const result = Array.from(categoryMap.values()).sort((a, b) => b.total_amount - a.total_amount);
    return result;
  };

  const calculateTotals = () => {
    if (cashFlowSummary.length === 0) {
      return { totalIncome: 0, totalExpenses: 0, totalNet: 0, avgSavingsRate: 0 };
    }
    
    const totalIncome = cashFlowSummary.reduce((sum, month) => {
      const income = (month.income || 0);
      const safeIncome = isNaN(income) ? 0 : income;
      return sum + safeIncome;
    }, 0);
    
    const totalExpenses = cashFlowSummary.reduce((sum, month) => {
      const expenses = (month.expenses || 0);
      const safeExpenses = isNaN(expenses) ? 0 : expenses;
      return sum + safeExpenses;
    }, 0);
    
    const totalNet = totalIncome - totalExpenses;
    const avgSavingsRate = totalIncome > 0 ? (totalNet / totalIncome) * 100 : 0;
    
    
    return { totalIncome, totalExpenses, totalNet, avgSavingsRate };
  };

  const { totalIncome, totalExpenses, totalNet, avgSavingsRate } = calculateTotals();

  // Generate yearly breakdown data
  const generateYearlyBreakdown = () => {
    // Get all unique years from the data
    const yearSet = new Set(cashFlowSummary.map(m => m.month.split('-')[0]));
    const years = Array.from(yearSet);
    const months: any[] = [];
    
    // For each year, generate all 12 months
    years.forEach(year => {
      for (let month = 0; month < 12; month++) {
        const monthDate = new Date(parseInt(year), month, 1);
        const monthKey = monthDate.toISOString().slice(0, 7); // YYYY-MM format
        
        // Find matching data from cashFlowSummary
        const monthData = cashFlowSummary.find(m => m.month.startsWith(monthKey)) || {
          month: monthKey,
          income: 0,
          expenses: 0,
          transfers: 0,
          net_cash_flow: 0,
          savings_rate: 0,
          transaction_count: 0
        };
        
        months.push({
          ...monthData,
          month: monthKey, // Ensure consistent format
          monthName: monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        });
      }
    });
    
    // Sort by date (oldest first)
    return months.sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());
  };

  const yearlyBreakdown = generateYearlyBreakdown();

  // Calculate average monthly metrics for completed months with data
  const calculateAverageMonthly = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    // Filter for completed months (past months) that have data
    const completedMonthsWithData = yearlyBreakdown.filter((month, index) => {
      const monthDate = new Date(currentYear, index, 1);
      const isPastMonth = monthDate < new Date(currentYear, currentMonth, 1);
      const hasData = (month.income || 0) > 0 || (month.expenses || 0) > 0;
      return isPastMonth && hasData;
    });

    if (completedMonthsWithData.length === 0) {
      return {
        avgIncome: 0,
        avgExpenses: 0,
        avgNetFlow: 0,
        avgSavingsRate: 0,
        avgTransactions: 0,
        monthsCount: 0
      };
    }

    const totalIncome = completedMonthsWithData.reduce((sum, month) => sum + (month.income || 0), 0);
    const totalExpenses = completedMonthsWithData.reduce((sum, month) => sum + (month.expenses || 0), 0);
    const totalNetFlow = totalIncome - totalExpenses;
    const totalTransactions = completedMonthsWithData.reduce((sum, month) => sum + (month.transaction_count || 0), 0);

    return {
      avgIncome: totalIncome / completedMonthsWithData.length,
      avgExpenses: totalExpenses / completedMonthsWithData.length,
      avgNetFlow: totalNetFlow / completedMonthsWithData.length,
      avgSavingsRate: totalIncome > 0 ? (totalNetFlow / totalIncome) * 100 : 0,
      avgTransactions: totalTransactions / completedMonthsWithData.length,
      monthsCount: completedMonthsWithData.length
    };
  };

  const averageMonthly = calculateAverageMonthly();

  // Calculate percentage of income spent/saved for each month
  const calculateIncomePercentages = () => {
    return yearlyBreakdown.map(month => {
      const income = month.income || 0;
      const expenses = Math.abs(month.expenses || 0); // Make positive for calculation
      const netFlow = month.net_cash_flow || 0;
      
      if (income <= 0) {
        return {
          month: month.month,
          percentSpent: 0,
          percentSaved: 0,
          hasData: false
        };
      }
      
      const percentSpent = (expenses / income) * 100;
      const percentSaved = (netFlow / income) * 100;
      
      return {
        month: month.month,
        percentSpent: Math.min(percentSpent, 100), // Cap at 100%
        percentSaved: Math.max(percentSaved, -100), // Allow negative but cap at -100%
        hasData: true
      };
    });
  };

  const incomePercentages = calculateIncomePercentages();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background-primary)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--primary-color)' }}></div>
          <p style={{ color: 'var(--text-primary)' }}>Loading cash flow data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background-primary)' }}>
        <div className="text-center">
          <div className="px-4 py-3 rounded mb-4" style={{ backgroundColor: 'var(--danger-color)', border: '1px solid var(--danger-color)', color: 'white' }}>
          <p>{error}</p>
          </div>
          <button 
            onClick={loadCashFlowData}
            className="font-medium py-2 px-4 rounded-lg transition-colors"
            style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-primary)' }}>
      {/* Header */}
      <div className="shadow-sm border-b" style={{ 
        backgroundColor: 'var(--background-card)', 
        borderColor: 'var(--border-color)',
        marginTop: '60px'
      }}>
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Cash Flow</h1>
              <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>Track your income, expenses, and savings over time</p>
            </div>
            {/* Removed CF2 badge */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Controls */}
        <div className="rounded-lg shadow-sm border p-6 mb-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Time Period</label>
          <select
            value={selectedPeriod}
            onChange={(e) => handlePeriodChange(e.target.value)}
                  className="block w-full sm:w-48 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{ 
                    backgroundColor: 'var(--input-background)', 
                    borderColor: 'var(--input-border)',
                    color: 'var(--text-primary)'
                  }}
          >
            <option value="last3months">Last 3 Months</option>
            <option value="last6months">Last 6 Months</option>
            <option value="last12months">Last 12 Months</option>
            {(transactionYears ?? []).map((y) => (
              <option key={y} value={String(y)}>
                {y}
              </option>
            ))}
            <option value="custom">Custom Range</option>
          </select>
        </div>

        {/* Custom Date Range Inputs */}
        {selectedPeriod === 'custom' && (
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Start Date</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="block w-full sm:w-48 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={{ 
                  backgroundColor: 'var(--input-background)', 
                  borderColor: 'var(--input-border)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>End Date</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="block w-full sm:w-48 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={{ 
                  backgroundColor: 'var(--input-background)', 
                  borderColor: 'var(--input-border)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>
          </div>
        )}

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>View Mode</label>
                <div className="flex rounded-md shadow-sm">
            <button
                    className={`px-4 py-2 text-sm font-medium rounded-l-md border ${
                      viewMode === 'monthly' 
                        ? 'text-white border-blue-600' 
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    style={{
                      backgroundColor: viewMode === 'monthly' ? 'var(--primary-color)' : 'var(--input-background)',
                      color: viewMode === 'monthly' ? 'white' : 'var(--text-primary)',
                      borderColor: viewMode === 'monthly' ? 'var(--primary-color)' : 'var(--input-border)'
                    }}
              onClick={() => setViewMode('monthly')}
            >
              Monthly View
            </button>
            <button
                    className={`px-4 py-2 text-sm font-medium rounded-r-md border-t border-r border-b ${
                      viewMode === 'yearly' 
                        ? 'text-white border-blue-600' 
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    style={{
                      backgroundColor: viewMode === 'yearly' ? 'var(--primary-color)' : 'var(--input-background)',
                      color: viewMode === 'yearly' ? 'white' : 'var(--text-primary)',
                      borderColor: viewMode === 'yearly' ? 'var(--primary-color)' : 'var(--input-border)'
                    }}
              onClick={() => setViewMode('yearly')}
            >
              Yearly View
            </button>
                </div>
              </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
        <div className="rounded-lg shadow-sm border mb-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
          <div className="border-b" style={{ borderColor: 'var(--border-color)' }}>
            <nav className="-mb-px flex space-x-8 px-6">
        <button
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent hover:border-gray-300'
                }`}
                style={{
                  color: activeTab === 'overview' ? 'var(--primary-color)' : 'var(--text-secondary)',
                  borderColor: activeTab === 'overview' ? 'var(--primary-color)' : 'transparent'
                }}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'income'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent hover:border-gray-300'
                }`}
                style={{
                  color: activeTab === 'income' ? 'var(--primary-color)' : 'var(--text-secondary)',
                  borderColor: activeTab === 'income' ? 'var(--primary-color)' : 'transparent'
                }}
          onClick={() => setActiveTab('income')}
        >
          Income
        </button>
        <button
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'expenses'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent hover:border-gray-300'
                }`}
                style={{
                  color: activeTab === 'expenses' ? 'var(--primary-color)' : 'var(--text-secondary)',
                  borderColor: activeTab === 'expenses' ? 'var(--primary-color)' : 'transparent'
                }}
          onClick={() => setActiveTab('expenses')}
        >
          Expenses
        </button>
            </nav>
          </div>
      </div>

      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="rounded-lg shadow-sm border p-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                      <span className="text-lg" style={{ color: 'var(--success-color)' }}>💰</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Total Income</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--success-color)' }}>{safeFormatCurrency(totalIncome)}</p>
                  </div>
                </div>
            </div>
            
              <div className="rounded-lg shadow-sm border p-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                      <span className="text-lg" style={{ color: 'var(--danger-color)' }}>💸</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Total Expenses</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--danger-color)' }}>{safeFormatCurrency(totalExpenses)}</p>
                  </div>
                </div>
            </div>
            
              <div className="rounded-lg shadow-sm border p-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ 
                      backgroundColor: totalNet >= 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)' 
                    }}>
                      <span className="text-lg" style={{ color: totalNet >= 0 ? 'var(--success-color)' : 'var(--danger-color)' }}>
                        {totalNet >= 0 ? '📈' : '📉'}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Net Cash Flow</p>
                    <p className="text-2xl font-bold" style={{ color: totalNet >= 0 ? 'var(--success-color)' : 'var(--danger-color)' }}>
                {safeFormatCurrency(totalNet)}
              </p>
                  </div>
                </div>
            </div>
            
              <div className="rounded-lg shadow-sm border p-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ 
                      backgroundColor: avgSavingsRate >= 0 ? 'rgba(99, 102, 241, 0.1)' : 'rgba(239, 68, 68, 0.1)' 
                    }}>
                      <span className="text-lg" style={{ color: avgSavingsRate >= 0 ? 'var(--primary-color)' : 'var(--danger-color)' }}>
                        {avgSavingsRate >= 0 ? '🎯' : '⚠️'}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Savings Rate</p>
                    <p className="text-2xl font-bold" style={{ color: avgSavingsRate >= 0 ? 'var(--primary-color)' : 'var(--danger-color)' }}>
                {avgSavingsRate.toFixed(1)}%
              </p>
                  </div>
                </div>
            </div>
          </div>

          {/* Breakdown View */}
            <div className="rounded-lg shadow-sm border mb-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
              <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <h2 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
                {viewMode === 'monthly' ? 'Monthly Breakdown' : 'Yearly Breakdown'}
              </h2>
            </div>
              <div className="p-6">
          {viewMode === 'monthly' ? (
            // Monthly View - Use yearly breakdown data for proper month ordering
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y" style={{ borderColor: 'var(--border-color)' }}>
                      <thead style={{ backgroundColor: 'var(--background-light)' }}>
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Month</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Income</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Expenses</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Net</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Savings Rate</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Transactions</th>
                  </tr>
                </thead>
                      <tbody className="divide-y" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                  {yearlyBreakdown
                    .filter(month => (month.income || 0) > 0 || (month.expenses || 0) > 0 || (month.transaction_count || 0) > 0)
                    .map((month) => (
                          <tr key={month.month} className="hover:bg-gray-800 hover:bg-opacity-50 cursor-pointer transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {month.monthName}
                      </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--success-color)' }}>
                              {safeFormatCurrency(month.income)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--danger-color)' }}>
                              {safeFormatCurrency(month.expenses)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ 
                              color: (month.net_cash_flow || 0) >= 0 ? 'var(--success-color)' : 'var(--danger-color)' 
                            }}>
                        {safeFormatCurrency(month.net_cash_flow)}
                      </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ 
                              color: (month.savings_rate || 0) >= 0 ? 'var(--success-color)' : 'var(--danger-color)' 
                            }}>
                        {(month.savings_rate || 0).toFixed(1)}%
                      </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                              {month.transaction_count || 0}
                            </td>
                    </tr>
                  ))}
                  
                  {/* Total Row */}
                  {(() => {
                    const monthsWithData = yearlyBreakdown.filter(month => 
                      (month.income || 0) > 0 || (month.expenses || 0) > 0 || (month.transaction_count || 0) > 0
                    );
                    
                    if (monthsWithData.length > 0) {
                      const totalIncome = monthsWithData.reduce((sum, month) => sum + (month.income || 0), 0);
                      const totalExpenses = monthsWithData.reduce((sum, month) => sum + (month.expenses || 0), 0);
                      const totalNet = totalIncome - totalExpenses;
                      const totalTransactions = monthsWithData.reduce((sum, month) => sum + (month.transaction_count || 0), 0);
                      const avgSavingsRate = totalIncome > 0 ? (totalNet / totalIncome) * 100 : 0;
                      
                      return (
                        <tr className="border-t-2 font-semibold" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--background-light)' }}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                            Total
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold" style={{ color: 'var(--success-color)' }}>
                            {safeFormatCurrency(totalIncome)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold" style={{ color: 'var(--danger-color)' }}>
                            {safeFormatCurrency(totalExpenses)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold" style={{ 
                            color: totalNet >= 0 ? 'var(--success-color)' : 'var(--danger-color)' 
                          }}>
                            {safeFormatCurrency(totalNet)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold" style={{ 
                            color: avgSavingsRate >= 0 ? 'var(--success-color)' : 'var(--danger-color)' 
                          }}>
                            {avgSavingsRate.toFixed(1)}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-bold" style={{ color: 'var(--text-secondary)' }}>
                            {totalTransactions}
                          </td>
                        </tr>
                      );
                    }
                    return null;
                  })()}
                </tbody>
              </table>
            </div>
          ) : (
                  // Yearly View - Keeping original structure for now
            <div className="yearly-breakdown">
              <div className="yearly-table-container">
                <table className="yearly-data-table">
                  <thead>
                    <tr>
                      <th className="metric-header">Metric</th>
                      {yearlyBreakdown.map((month) => (
                        <th key={month.month} className="month-header">
                          {month.monthName}
                        </th>
                      ))}
                      <th className="ytd-header">YTD</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="income-row">
                      <td className="metric-label">Income</td>
                      {yearlyBreakdown.map((month) => (
                        <td key={month.month} className="positive">
                          {safeFormatCurrency(month.income)}
                        </td>
                      ))}
                      <td className="ytd-cell positive">
                        {safeFormatCurrency(yearlyBreakdown.reduce((sum, month) => sum + (month.income || 0), 0))}
                      </td>
                    </tr>
                    <tr className="expenses-row">
                      <td className="metric-label">Expenses</td>
                      {yearlyBreakdown.map((month) => (
                        <td key={month.month} className="negative">
                          {safeFormatCurrency(month.expenses)}
                        </td>
                      ))}
                      <td className="ytd-cell negative">
                        {safeFormatCurrency(yearlyBreakdown.reduce((sum, month) => sum + (month.expenses || 0), 0))}
                      </td>
                    </tr>
                    <tr className="net-row">
                      <td className="metric-label">Net Cash Flow</td>
                      {yearlyBreakdown.map((month) => {
                        const netFlow = month.net_cash_flow || 0;
                        return (
                          <td key={month.month} className={netFlow >= 0 ? 'positive' : 'negative'}>
                            {safeFormatCurrency(netFlow)}
                          </td>
                        );
                      })}
                      <td className={`ytd-cell ${totalNet >= 0 ? 'positive' : 'negative'}`}>
                        {safeFormatCurrency(totalNet)}
                      </td>
                    </tr>
                    <tr className="savings-row">
                      <td className="metric-label">Savings Rate</td>
                      {yearlyBreakdown.map((month) => {
                        const savingsRate = month.savings_rate || 0;
                        return (
                          <td key={month.month} className={savingsRate >= 0 ? 'positive' : 'negative'}>
                            {savingsRate.toFixed(1)}%
                          </td>
                        );
                      })}
                      <td className={`ytd-cell ${avgSavingsRate >= 0 ? 'positive' : 'negative'}`}>
                        {avgSavingsRate.toFixed(1)}%
                      </td>
                    </tr>
                    <tr className="transactions-row">
                      <td className="metric-label">Transactions</td>
                      {yearlyBreakdown.map((month) => (
                        <td key={month.month}>
                          {Number(month.transaction_count) || 0}
                        </td>
                      ))}
                      <td className="ytd-cell">
                        {(() => {
                          // Calculate YTD total from the actual cash flow summary data
                          const ytdTransactions = cashFlowSummary.reduce((sum, month) => {
                            const count = Number(month.transaction_count) || 0;
                            return sum + count;
                          }, 0);
                          return ytdTransactions;
                        })()}
                      </td>
                    </tr>
                    
                    {/* Income Percentage Rows */}
                    <tr className="income-percentage-row">
                      <td className="metric-label percentage-label">% Income Spent</td>
                      {incomePercentages.map((month) => (
                        <td key={month.month} className="percentage-cell">
                          {month.hasData ? `${month.percentSpent.toFixed(1)}%` : '-'}
                        </td>
                      ))}
                      <td className="ytd-cell percentage-ytd">
                        {(() => {
                          const totalIncome = yearlyBreakdown.reduce((sum, month) => sum + (month.income || 0), 0);
                          const totalExpenses = yearlyBreakdown.reduce((sum, month) => sum + Math.abs(month.expenses || 0), 0);
                          if (totalIncome === 0) return '-';
                          const avgPercentSpent = (totalExpenses / totalIncome) * 100;
                          return `${Math.min(avgPercentSpent, 100).toFixed(1)}%`;
                        })()}
                      </td>
                    </tr>
                    <tr className="income-percentage-row">
                      <td className="metric-label percentage-label">% Income Saved</td>
                      {incomePercentages.map((month) => (
                        <td key={month.month} className={`percentage-cell ${month.percentSaved < 0 ? 'negative' : 'positive'}`}>
                          {month.hasData ? `${month.percentSaved.toFixed(1)}%` : '-'}
                        </td>
                      ))}
                      <td className="ytd-cell percentage-ytd">
                        {(() => {
                          const totalIncome = yearlyBreakdown.reduce((sum, month) => sum + (month.income || 0), 0);
                          const totalNetFlow = yearlyBreakdown.reduce((sum, month) => sum + (month.net_cash_flow || 0), 0);
                          if (totalIncome <= 0) return '-';
                          const avgPercentSaved = (totalNetFlow / totalIncome) * 100;
                          return `${avgPercentSaved.toFixed(1)}%`;
                        })()}
                      </td>
                    </tr>
                    
                  </tbody>
                </table>
              </div>
              
              {/* Average Monthly Summary */}
              {averageMonthly.monthsCount > 0 && (
                <div className="average-monthly-summary">
                  <h4>Average Monthly Metrics ({averageMonthly.monthsCount} completed months)</h4>
                  <div className="average-metrics-grid">
                    <div className="average-metric">
                      <span className="metric-name">Avg Income:</span>
                      <span className="metric-value positive">{safeFormatCurrency(averageMonthly.avgIncome)}</span>
                    </div>
                    <div className="average-metric">
                      <span className="metric-name">Avg Expenses:</span>
                      <span className="metric-value negative">{safeFormatCurrency(averageMonthly.avgExpenses)}</span>
                    </div>
                    <div className="average-metric">
                      <span className="metric-name">Avg Net Flow:</span>
                      <span className={`metric-value ${averageMonthly.avgNetFlow >= 0 ? 'positive' : 'negative'}`}>
                        {safeFormatCurrency(averageMonthly.avgNetFlow)}
                      </span>
                    </div>
                    <div className="average-metric">
                      <span className="metric-name">Avg Savings Rate:</span>
                      <span className={`metric-value ${averageMonthly.avgSavingsRate >= 0 ? 'positive' : 'negative'}`}>
                        {averageMonthly.avgSavingsRate.toFixed(1)}%
                      </span>
                    </div>
                    <div className="average-metric">
                      <span className="metric-name">Avg Transactions:</span>
                      <span className="metric-value">{Math.round(averageMonthly.avgTransactions)}</span>
                    </div>
                    <div className="average-metric">
                      <span className="metric-name">Avg % Income Spent:</span>
                      <span className="metric-value negative">
                        {averageMonthly.avgIncome > 0 ? `${Math.min((averageMonthly.avgExpenses / averageMonthly.avgIncome) * 100, 100).toFixed(1)}%` : '-'}
                      </span>
                    </div>
                    <div className="average-metric">
                      <span className="metric-name">Avg % Income Saved:</span>
                      <span className={`metric-value ${averageMonthly.avgNetFlow >= 0 ? 'positive' : 'negative'}`}>
                        {averageMonthly.avgIncome > 0 ? `${Math.max((averageMonthly.avgNetFlow / averageMonthly.avgIncome) * 100, -100).toFixed(1)}%` : '-'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

          {/* Category Breakdown */}
            <div className="rounded-lg shadow-sm border" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
              <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <h2 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>Spending by Category</h2>
            </div>
              <div className="p-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y" style={{ borderColor: 'var(--border-color)' }}>
                    <thead style={{ backgroundColor: 'var(--background-light)' }}>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Percentage</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Transactions</th>
                    </tr>
                  </thead>
                    <tbody className="divide-y" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                    {categoryBreakdown.map((category) => (
                        <tr key={category.category_id} className="hover:bg-gray-50" style={{ borderColor: 'var(--border-color)' }}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div 
                                className="w-4 h-4 rounded-full mr-3"
                              style={{ backgroundColor: category.category_color }}
                            ></div>
                              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{category.category_name}</span>
                          </div>
                        </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--danger-color)' }}>
                            {safeFormatCurrency(category.total_amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {category.percentage.toFixed(1)}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {category.transaction_count}
                          </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Income Tab Content */}
      {activeTab === 'income' && (
          <>
          {/* Income Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="rounded-lg shadow-sm border p-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                      <span className="text-lg" style={{ color: 'var(--success-color)' }}>💰</span>
            </div>
            </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Total Income</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--success-color)' }}>{safeFormatCurrency(totalIncome)}</p>
            </div>
            </div>
              </div>
              
              <div className="rounded-lg shadow-sm border p-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)' }}>
                      <span className="text-lg" style={{ color: 'var(--primary-color)' }}>📊</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Income Sources</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--primary-color)' }}>{incomeSources.length}</p>
                  </div>
            </div>
          </div>

              <div className="rounded-lg shadow-sm border p-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                      <span className="text-lg" style={{ color: 'var(--success-color)' }}>📈</span>
              </div>
                </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Avg Income</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--success-color)' }}>
                      {cashFlowSummary.length > 0 
                        ? safeFormatCurrency(totalIncome / cashFlowSummary.length)
                        : safeFormatCurrency(0)
                      }
                    </p>
              </div>
            </div>
              </div>
            </div>

            {/* Income Sources Breakdown */}
            <div className="rounded-lg shadow-sm border mb-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
              <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <h2 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>Income Sources</h2>
              </div>
              <div className="p-6">
                {processIncomeData().length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y" style={{ borderColor: 'var(--border-color)' }}>
                      <thead style={{ backgroundColor: 'var(--background-light)' }}>
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Source</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Total Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Transactions</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Last Transaction</th>
                      </tr>
                    </thead>
                      <tbody className="divide-y" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                        {processIncomeData().map((category) => {
                          console.log('Category:', category.category_name, 'is_reimbursement:', category.is_reimbursement, 'type:', typeof category.is_reimbursement);
                          return (
                          <React.Fragment key={category.category_id || 'uncategorized'}>
                            {/* Category Row */}
                            <tr 
                              className={`hover:bg-gray-800 hover:bg-opacity-50 cursor-pointer transition-colors ${selectedIncomeSource === category.category_name ? 'bg-blue-900 bg-opacity-30 border-l-4 border-l-blue-500' : ''}`} 
                              style={{ borderColor: 'var(--border-color)' }}
                          onClick={(e) => {
                            e.stopPropagation();
                                console.log('Income category clicked:', category.category_name);
                            setSelectedIncomeSource(
                                  selectedIncomeSource === category.category_name ? null : category.category_name
                            );
                          }}
                          title="Click to view transactions"
                        >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  {category.subcategories.length > 0 && (
                                    <button
                                      onClick={(e) => {
                            e.stopPropagation();
                                        const newExpanded = new Set(expandedIncomeCategories);
                                        if (newExpanded.has(category.category_id || 'uncategorized')) {
                                          newExpanded.delete(category.category_id || 'uncategorized');
                                        } else {
                                          newExpanded.add(category.category_id || 'uncategorized');
                                        }
                                        setExpandedIncomeCategories(newExpanded);
                                      }}
                                      className="mr-2 p-1 hover:bg-gray-700 rounded"
                                      title={expandedIncomeCategories.has(category.category_id || 'uncategorized') ? "Collapse subcategories" : "Expand subcategories"}
                                    >
                                      <svg 
                                        className={`w-4 h-4 transition-transform ${expandedIncomeCategories.has(category.category_id || 'uncategorized') ? 'rotate-90' : ''}`} 
                                        fill="currentColor" 
                                        viewBox="0 0 20 20"
                                        style={{ color: 'var(--text-secondary)' }}
                                      >
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                      </svg>
                                    </button>
                                  )}
                                  <div 
                                    className="w-4 h-4 rounded-full mr-3"
                                    style={{ backgroundColor: category.category_color || '#10B981' }}
                                  ></div>
                                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                    {category.category_name}
                                  </span>
                                  {(category.is_reimbursement === true || category.is_reimbursement === 1 || category.is_reimbursement === 'true' || category.is_reimbursement === '1') && (
                                    <span className="ml-2 text-xs px-2 py-1 rounded" style={{ 
                                      backgroundColor: 'rgba(245, 158, 11, 0.1)', 
                                      color: '#f59e0b',
                                      fontWeight: '500'
                                    }}>
                                      🏥 Excluded from Total Income
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: (category.is_reimbursement === true || category.is_reimbursement === 1 || category.is_reimbursement === 'true' || category.is_reimbursement === '1') ? 'var(--text-muted)' : 'var(--success-color)' }}>
                                {safeFormatCurrency(category.total_amount)}
                                {(category.is_reimbursement === true || category.is_reimbursement === 1 || category.is_reimbursement === 'true' || category.is_reimbursement === '1') && (
                                  <div className="text-xs mt-1" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                    (not included in total)
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                                {category.transaction_count}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                                {new Date(category.last_transaction).toLocaleDateString()}
                              </td>
                            </tr>
                            
                            {/* Subcategory Rows */}
                            {expandedIncomeCategories.has(category.category_id || 'uncategorized') && category.subcategories
                              .sort((a: any, b: any) => (b.total_amount || 0) - (a.total_amount || 0))
                              .map((subcategory: any) => (
                              <tr 
                                key={`${category.category_id || 'uncategorized'}-${subcategory.subcategory_id}`}
                                className={`hover:bg-gray-800 hover:bg-opacity-20 cursor-pointer transition-colors ${selectedIncomeSource === `${category.category_name} - ${subcategory.subcategory_name}` ? 'bg-blue-900 bg-opacity-20 border-l-4 border-l-blue-400' : ''}`}
                                style={{ borderColor: 'var(--border-color)' }}
                                onClick={(e) => {
                            e.stopPropagation();
                                  console.log('Income subcategory clicked:', `${category.category_name} - ${subcategory.subcategory_name}`);
                            setSelectedIncomeSource(
                                    selectedIncomeSource === `${category.category_name} - ${subcategory.subcategory_name}` ? null : `${category.category_name} - ${subcategory.subcategory_name}`
                                  );
                                }}
                                title="Click to view transactions"
                              >
                                <td className="px-6 py-3 whitespace-nowrap">
                                  <div className="flex items-center pl-12">
                                    <div 
                                      className="w-3 h-3 rounded-full mr-3"
                                      style={{ backgroundColor: category.category_color || '#10B981', opacity: 0.7 }}
                                    ></div>
                                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                      {subcategory.subcategory_name}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--success-color)' }}>
                                  {safeFormatCurrency(subcategory.total_amount)}
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                                  {subcategory.transaction_count}
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                                  {new Date(subcategory.last_transaction).toLocaleDateString()}
                          </td>
                        </tr>
                            ))}
                          </React.Fragment>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">💰</div>
                    <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>No income data available</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      Connect your bank accounts to see your income sources and patterns.
                    </p>
            </div>
          )}
              </div>
            </div>

          {/* Filtered Income Transactions by Source */}
          {selectedIncomeSource && (
              <div className="rounded-lg shadow-sm border mb-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
                      Transactions for: {selectedIncomeSource}
                    </h2>
                    <button 
                        className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50 transition-colors"
                        style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                      onClick={() => setSelectedIncomeSource(null)}
                    >
                      Show All Income
                    </button>
                  </div>
                  {(() => {
                    const summary = getRefundSummary(selectedIncomeSource);
                    return (
                      <div className="flex items-center space-x-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <div>
                          <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                            {summary.totalTransactions} transactions
                          </span>
                          <span className="ml-2" style={{ color: 'var(--success-color)' }}>
                            {safeFormatCurrency(summary.totalAmount)}
                          </span>
                        </div>
                        {summary.refundCount > 0 && (
                          <div>
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                              ({summary.refundCount} refund{summary.refundCount !== 1 ? 's' : ''} totalling {safeFormatCurrency(summary.refundTotal)} not included as income)
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="p-6">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y" style={{ borderColor: 'var(--border-color)' }}>
                      <thead style={{ backgroundColor: 'var(--background-light)' }}>
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Description</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Account</th>
                          <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider"></th>
                      </tr>
                    </thead>
                      <tbody className="divide-y" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                      {getTransactionsByIncomeSource(selectedIncomeSource).map((transaction) => (
                          <tr key={transaction.id} style={{ borderColor: 'var(--border-color)' }}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                              {new Date(transaction.date).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                              {transaction.merchant_name || transaction.description}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {transaction.category_name ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span>
                                  {transaction.is_refund ? (
                                    `💰 Refund of ${transaction.category_name}${transaction.subcategory_name ? ` - ${transaction.subcategory_name}` : ''}`
                                  ) : transaction.is_insurance_reimbursement ? (
                                    `🏥 Insurance Reimbursement: ${transaction.category_name}${transaction.subcategory_name ? ` - ${transaction.subcategory_name}` : ''}`
                                  ) : (
                                    `${transaction.category_name}${transaction.subcategory_name && ` > ${transaction.subcategory_name}`}`
                                  )}
                                </span>
                                {transaction.is_insurance_reimbursement && transaction.reimbursed_expenses && transaction.reimbursed_expenses.length > 0 && (
                                  <div className="text-xs mt-1" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                    Reimbursed: {transaction.reimbursed_expenses.map((reimb: any, idx: number) => (
                                      <span key={idx}>
                                        {idx > 0 && ', '}
                                        {reimb.category_name || 'Uncategorized'}{reimb.subcategory_name ? ` - ${reimb.subcategory_name}` : ''} ({safeFormatCurrency(reimb.covered_amount)})
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                                <span style={{ color: 'var(--warning-color)' }}>Uncategorized</span>
                            )}
                          </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--success-color)' }}>
                              {safeFormatCurrency(transaction.amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                              {transaction.account_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              {transaction.notes && (
                                <div className="notes-button-container">
                                  <span className="text-base" style={{ cursor: 'help', display: 'inline-block' }} title="View notes">
                                    📝
                                  </span>
                                  <div className="notes-tooltip">
                                    {transaction.notes}
                                  </div>
                                </div>
                              )}
                            </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

            {/* Income Pie Chart */}
            <div className="rounded-lg shadow-sm border" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
              <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>Income Distribution</h2>
                  <span className="text-sm px-2 py-1 rounded-full" style={{ 
                    backgroundColor: 'var(--primary-color)', 
                    color: 'white' 
                  }}>
                    {cashFlowPeriodDisplayLabel(selectedPeriod, customStartDate, customEndDate)}
                            </span>
              </div>
            </div>
              <div className="p-6">
                {processIncomeData().length > 0 ? (
                  <div className="max-w-3xl">
                    <h3 className="text-sm font-medium mb-4" style={{ color: 'var(--text-primary)' }}>Income Sources</h3>
                    <div className="space-y-4">
                      {processIncomeData()
                        .map((s, i) => ({
                          name: s.category_name,
                          amount: parseFloat(String(s.total_amount)) || 0,
                          count: Number(s.transaction_count) || 0,
                          color: s.category_color || ['#10B981','#3B82F6','#F59E0B','#EF4444','#8B5CF6','#06B6D4','#F97316','#84CC16','#EC4899','#6366F1'][i % 10]
                        }))
                        .sort((a,b) => b.amount - a.amount)
                        .map((item, idx) => {
                          const pct = totalIncome > 0 ? (item.amount / totalIncome) * 100 : 0;
                          return (
                            <div key={idx} className="flex items-center">
                              <div className="w-40 pr-3 flex items-center space-x-2">
                                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }}></span>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{item.name}</p>
                                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{item.count} transaction{item.count !== 1 ? 's' : ''}</p>
          </div>
              </div>
                              <div className="flex-1 mx-3">
                                <div className="h-2 rounded-full" style={{ backgroundColor: 'var(--background-light)' }}>
                                  <div className="h-2 rounded-full" style={{ width: `${Math.min(pct, 100).toFixed(1)}%`, backgroundColor: item.color }}></div>
                </div>
              </div>
                              <div className="w-32 text-right">
                                <p className="text-sm font-medium" style={{ color: 'var(--success-color)' }}>{safeFormatCurrency(item.amount)}</p>
                                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{pct.toFixed(1)}%</p>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-4xl mb-4">📊</div>
                    <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>No income data available</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      Connect your bank accounts to see your income distribution.
                    </p>
            </div>
          )}
        </div>
            </div>
          </>
      )}

      {/* Expenses Tab Content */}
      {activeTab === 'expenses' && (
          <>
          {/* Expenses Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="rounded-lg shadow-sm border p-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                      <span className="text-lg" style={{ color: 'var(--danger-color)' }}>💸</span>
            </div>
            </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Total Expenses</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--danger-color)' }}>{safeFormatCurrency(totalExpenses)}</p>
            </div>
            </div>
          </div>

              <div className="rounded-lg shadow-sm border p-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)' }}>
                      <span className="text-lg" style={{ color: 'var(--primary-color)' }}>🏷️</span>
              </div>
                </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Categories</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--primary-color)' }}>{categoryBreakdown.length}</p>
              </div>
            </div>
              </div>
              
              <div className="rounded-lg shadow-sm border p-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                      <span className="text-lg" style={{ color: 'var(--danger-color)' }}>📉</span>
              </div>
                </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Avg Expenses</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--danger-color)' }}>
                      {cashFlowSummary.length > 0 
                        ? safeFormatCurrency(totalExpenses / cashFlowSummary.length)
                        : safeFormatCurrency(0)
                      }
                    </p>
              </div>
            </div>
              </div>
            </div>

            {/* Expenses Drill Down */}
            <div className="rounded-lg shadow-sm border mb-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
              <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <h2 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>Expenses by Category</h2>
              </div>
              <div className="p-6">
                {processExpensesData().length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y" style={{ borderColor: 'var(--border-color)' }}>
                      <thead style={{ backgroundColor: 'var(--background-light)' }}>
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Total Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Transactions</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Last Transaction</th>
                      </tr>
                    </thead>
                      <tbody className="divide-y" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                        {processExpensesData().map((category) => (
                          <React.Fragment key={category.category_id}>
                            {/* Category Row */}
                            <tr 
                              className={`hover:bg-gray-800 hover:bg-opacity-50 cursor-pointer transition-colors ${selectedExpenseCategory === category.category_name ? 'bg-blue-900 bg-opacity-30 border-l-4 border-l-blue-500' : ''}`} 
                              style={{ borderColor: 'var(--border-color)' }}
                              onClick={(e) => {
                                e.stopPropagation();
                            console.log('Expense category clicked:', category.category_name);
                            setSelectedExpenseCategory(
                              selectedExpenseCategory === category.category_name ? null : category.category_name
                            );
                          }}
                          title="Click to view transactions"
                        >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  {category.subcategories.length > 0 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const newExpanded = new Set(expandedCategories);
                                        if (newExpanded.has(category.category_id)) {
                                          newExpanded.delete(category.category_id);
                                        } else {
                                          newExpanded.add(category.category_id);
                                        }
                                        setExpandedCategories(newExpanded);
                                      }}
                                      className="mr-2 p-1 hover:bg-gray-700 rounded"
                                      title={expandedCategories.has(category.category_id) ? "Collapse subcategories" : "Expand subcategories"}
                                    >
                                      <svg 
                                        className={`w-4 h-4 transition-transform ${expandedCategories.has(category.category_id) ? 'rotate-90' : ''}`} 
                                        fill="currentColor" 
                                        viewBox="0 0 20 20"
                                        style={{ color: 'var(--text-secondary)' }}
                                      >
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                      </svg>
                                    </button>
                                  )}
                                  <div 
                                    className="w-4 h-4 rounded-full mr-3"
                                style={{ backgroundColor: category.category_color }}
                              ></div>
                                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                    {category.category_name}
                                  </span>
                            </div>
                          </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--danger-color)' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                  <span>{safeFormatCurrency(category.total_amount)}</span>
                                  {(() => {
                                    const reimbAmount = category.total_reimbursed_amount ? parseFloat(String(category.total_reimbursed_amount)) : 0;
                                    if (reimbAmount > 0) {
                                      const totalExpense = parseFloat(String(category.total_amount)) + reimbAmount;
                                      return (
                                        <span className="text-xs" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                          Total expense: {safeFormatCurrency(totalExpense)}, Reimbursed: {safeFormatCurrency(category.total_reimbursed_amount)}
                                        </span>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                                {category.transaction_count}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                                {new Date(category.last_transaction).toLocaleDateString()}
                              </td>
                            </tr>
                            
                            {/* Subcategory Rows */}
                            {expandedCategories.has(category.category_id) && category.subcategories
                              .sort((a: any, b: any) => (b.total_amount || 0) - (a.total_amount || 0))
                              .map((subcategory: any) => (
                              <tr 
                                key={`${category.category_id}-${subcategory.subcategory_id}`}
                                className={`hover:bg-gray-800 hover:bg-opacity-20 cursor-pointer transition-colors ${selectedExpenseCategory === `${category.category_name} - ${subcategory.subcategory_name}` ? 'bg-blue-900 bg-opacity-20 border-l-4 border-l-blue-400' : ''}`}
                                style={{ borderColor: 'var(--border-color)' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log('Expense subcategory clicked:', `${category.category_name} - ${subcategory.subcategory_name}`);
                                  setSelectedExpenseCategory(
                                    selectedExpenseCategory === `${category.category_name} - ${subcategory.subcategory_name}` ? null : `${category.category_name} - ${subcategory.subcategory_name}`
                                  );
                                }}
                                title="Click to view transactions"
                              >
                                <td className="px-6 py-3 whitespace-nowrap">
                                  <div className="flex items-center pl-12">
                                    <div 
                                      className="w-3 h-3 rounded-full mr-3"
                                      style={{ backgroundColor: category.category_color, opacity: 0.7 }}
                                    ></div>
                                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                      {subcategory.subcategory_name}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--danger-color)' }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                    <span>{safeFormatCurrency(subcategory.total_amount)}</span>
                                    {(() => {
                                      const reimbAmount = subcategory.total_reimbursed_amount ? parseFloat(String(subcategory.total_reimbursed_amount)) : 0;
                                      if (reimbAmount > 0) {
                                        const totalExpense = parseFloat(String(subcategory.total_amount)) + reimbAmount;
                                        return (
                                          <span className="text-xs" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                            Total expense: {safeFormatCurrency(totalExpense)}, Reimbursed: {safeFormatCurrency(subcategory.total_reimbursed_amount)}
                                          </span>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                                  {subcategory.transaction_count}
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                                  {new Date(subcategory.last_transaction).toLocaleDateString()}
                          </td>
                        </tr>
                            ))}
                          </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">💸</div>
                    <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>No expense data available</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      Connect your bank accounts to see your spending patterns by category.
                    </p>
            </div>
          )}
            </div>
          </div>

          {/* Filtered Expense Transactions by Category */}
          {selectedExpenseCategory && (
              <div className="rounded-lg shadow-sm border mb-6" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
                      Transactions for: {selectedExpenseCategory}
                    </h2>
                    <button 
                        className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50 transition-colors"
                        style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                      onClick={() => setSelectedExpenseCategory(null)}
                    >
                      Show All Expenses
                    </button>
                  </div>
                  {(() => {
                    const summary = getExpenseRefundSummary(selectedExpenseCategory);
                    return (
                      <div className="flex items-center space-x-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <div>
                          <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                            {summary.totalTransactions} transactions
                          </span>
                          <span className="ml-2" style={{ color: 'var(--danger-color)' }}>
                            {safeFormatCurrency(summary.totalAmount)}
                          </span>
                        </div>
                        {summary.refundCount > 0 && (
                          <div>
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                              ({summary.refundCount} refund{summary.refundCount !== 1 ? 's' : ''} totalling {safeFormatCurrency(summary.refundTotal)} not included as income)
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="p-6">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y" style={{ borderColor: 'var(--border-color)' }}>
                      <thead style={{ backgroundColor: 'var(--background-light)' }}>
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Description</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>Account</th>
                          <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider"></th>
                      </tr>
                    </thead>
                      <tbody className="divide-y" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                      {getTransactionsByExpenseCategory(selectedExpenseCategory).map((transaction) => {
                        // Calculate the amount to display (split amount if applicable, otherwise full transaction amount)
                        const splitAmount = getSplitAmountForCategory(transaction, selectedExpenseCategory);
                        const displayAmount = splitAmount !== null ? -splitAmount : transaction.amount; // Make negative for expenses
                        const displayCategoryName = getCategoryNameForDisplay(transaction, selectedExpenseCategory);
                        const isFromSplit = splitAmount !== null;
                        
                        return (
                          <tr key={transaction.id} style={{ borderColor: 'var(--border-color)' }}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                              {new Date(transaction.date).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                              {transaction.merchant_name || transaction.description}
                              {isFromSplit && transaction.splits && transaction.splits.length > 1 && (
                                <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                                  (Split)
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                              {transaction.is_refund ? (
                                <span>💰 Refund of {displayCategoryName}</span>
                              ) : (
                                <span>{displayCategoryName}</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--danger-color)' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                <span>{safeFormatCurrency(displayAmount)}</span>
                                {transaction.total_reimbursed_amount && transaction.total_reimbursed_amount > 0 && (
                                  <span 
                                    style={{ 
                                      fontSize: '11px', 
                                      color: transaction.total_reimbursed_amount >= Math.abs(transaction.amount) ? '#10b981' : '#f59e0b',
                                      fontWeight: '500'
                                    }}
                                    title={`Reimbursed: ${safeFormatCurrency(transaction.total_reimbursed_amount)} of ${safeFormatCurrency(Math.abs(transaction.amount))}`}
                                  >
                                    🏥 {transaction.total_reimbursed_amount >= Math.abs(transaction.amount) ? 'Fully' : 'Partially'} Reimbursed
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                              {transaction.account_name || 'Unknown Account'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              {transaction.notes && (
                                <div className="notes-button-container">
                                  <span className="text-base" style={{ cursor: 'help', display: 'inline-block' }} title="View notes">
                                    📝
                                  </span>
                                  <div className="notes-tooltip">
                                    {transaction.notes}
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

            {/* Expenses Chart */}
            <div className="rounded-lg shadow-sm border" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
              <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <h2 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>Expenses Over Time</h2>
            </div>
              <div className="p-6">
                <div className="h-64 flex items-center justify-center">
                  <p style={{ color: 'var(--text-secondary)' }}>Expenses chart will be implemented here</p>
              </div>
            </div>
          </div>
          </>
      )}

      {/* Empty State */}
      {cashFlowSummary.length === 0 && (
        <EmptyState
          icon="💸"
          headline="Your cash flow picture starts here"
          description="Connect a bank account or import transactions to see where your money goes."
          primaryAction={{ label: 'Connect a Bank', onClick: () => navigate('/banks') }}
          secondaryAction={{ label: 'Import Transactions', onClick: () => navigate('/import') }}
        />
      )}

      </div>
    </div>
  );
};

export default CashFlow2;
